import { InfoCircle } from "@dreamhub/designsystem/icons";
import { useFieldItem } from "@/components/controls/Form/hooks/useFieldItem";
import {
  FormCommonProps,
  FormFieldNewType
} from "@/components/controls/Form/types";
import IconContainer from "@/components/ui/icon-container";
import { IconButton } from "@/components/ui/icon-button";
import Text from "@/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/utils/utils";
import React from "react";

interface FormFieldNewProps extends FormCommonProps {
  field: FormFieldNewType;
  allFields?: FormFieldNewType[];
}

const FormFieldNew: React.FC<FormFieldNewProps> = ({
  field,
  setValue,
  watch,
  register,
  allFields
}) => {
  const { renderField } = useFieldItem();
  const isLabelPositionLeft = field.labelPosition === "left";
  const fieldLayout = field.layout ?? "full";
  const suppressLabel = field.suppressLabel ?? false;

  // Ensure all required props are available before rendering the field
  if (!setValue || !watch || !register) {
    console.error("Required props missing in FormFieldNew:", {
      setValue,
      watch,
      register
    });
    return null;
  }

  return (
    <div
      className={cn(
        "flex flex-col justify-start",
        suppressLabel
          ? "w-full"
          : fieldLayout === "flex"
            ? "w-auto min-w-0"
            : fieldLayout === "half"
              ? "flex-1"
              : "w-full",
        !suppressLabel &&
          (isLabelPositionLeft
            ? "flex flex-row items-start gap-4"
            : "space-y-1"),
        field?.fieldClassName
      )}
    >
      {!suppressLabel && field.label && (
        <label
          className={cn(
            "flex shrink-0 items-center justify-between",
            isLabelPositionLeft ? "w-[200px] pt-2" : "min-h-6",
            field?.labelClassName
          )}
        >
          <div
            className={cn(
              "flex flex-row items-center w-full",
              isLabelPositionLeft && "gap-2"
            )}
          >
            <div className="flex flex-col">
              <div className="flex flex-row">
                <Text
                  data-test={`${field.name}-label`}
                  variant={"bodySecondary"}
                >
                  {field.label}
                </Text>
                {(field.required || field?.isDependentRequired) && (
                  <span className="text-primary ml-1">*</span>
                )}
              </div>
              {field?.subLabel && (
                <Text variant="bodyTertiary">{field?.subLabel}</Text>
              )}
            </div>
            {field.helpText && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <IconButton
                    variant="iconNoBg"
                    size="sm"
                    iconColor="secondary"
                    className="!size-5"
                  >
                    <InfoCircle />
                  </IconButton>
                </TooltipTrigger>
                <TooltipContent maxWidth={13}>
                  <Text variant="smBody">{field.helpText}</Text>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </label>
      )}
      <div className={cn("flex-1 min-w-0 min-h-14", field?.fieldContainerClassName)}>
        {renderField(
          field,
          setValue,
          watch,
          field?.hasError ?? false,
          register,
          allFields
        )}
        {field.errorMessage && field.type !== "multiEmail" && (
          <div>
            <Text variant="smBody" className="text-red-400">
              {field.errorMessage}
            </Text>
          </div>
        )}
        {field.hintText && (
          <div className="flex items-center gap-1 p-1">
            <IconContainer size="sm" color="secondary">
              <InfoCircle />
            </IconContainer>
            <Text
              data-test={`${field.name}-hint-text`}
              variant={"bodySecondary"}
              className="mt-[0.5px]"
            >
              {field.hintText}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormFieldNew;
